import React, { Component } from 'react';
import { Typography } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import * as Yup from "yup";
import { Link } from 'react-router-dom';
import axios from "axios";
import { Formik, Form } from "formik";
import { Dispatch, bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// Contexts
import { withTranslate, withTenant, withNavigation } from "../../../core/TenantProvider";
import { getConfigProperty } from '../../../core/EnvironmentConfig/EnvironmentConfig';
// Components - Atoms, Molecules, Organisms, Pages
import APInput from "../../../components/atoms/APInput";
import APButton from "../../../components/atoms/APButton";
import APHeading from "../../../components/atoms/APHeading";
import APLabel from "../../../components/atoms/APLabel";
import APLoadingSpinner from "../../../components/atoms/APLoadingSpinner";
import APResetImagePlaceholder from "../../../components/atoms/APResetImagePlaceholder";
import APErrorMessage from "../../../components/atoms/APErrorMessage";
import { ReactComponent as VerifiedEmail } from "../../../components/atoms/APResetImagePlaceholder/assets/VerifiedEmailIcon.svg";
// Files in the same folder
import ResetPasswordContainerStyles from "../styles/PasswordReset.style";
//Redux
import { actions as servicesDowntimeAction } from '../../../redux/modules/servicesDowntime';
// Utils
import GetBasicDetails from "../../../core/utils/GetBasicDetails";

const {
	basicAuthToken: BASIC_AUTH_TOKEN
} = GetBasicDetails();

interface ResetPasswordContainerProps
	extends WithStyles<typeof ResetPasswordContainerStyles> {
	classes: any;
	navigate: any;
	location?: any;
	values: any;
	touched: any;
	errors: any;
	dirty: boolean;
	isValid: boolean;
	handleChange: any;
	handleBlur: any;
	handleSubmit: any;
	translate: Function;
	onChange?: any;
	settings: any,
	getServicesErrorObj: Function
}

interface ResetPasswordContainerState {
	currentStep: number;
	lastStep: number;
	email: string;
	loaded: boolean;
	isError: boolean;
	serverError: string;
	resendCount: number;
	resendDisabled: boolean;
	secondMessage: boolean;
	resendPinLoader: boolean;
	showPasswordPolicyError: boolean;
}

class ResetPasswordContainer extends Component<
	ResetPasswordContainerProps,
	ResetPasswordContainerState
> {
	state = {
		currentStep: 0,
		lastStep: 2,
		email: "",
		loaded: false,
		isError: false,
		serverError: "",
		resendCount: 0,
		resendDisabled: false,
		secondMessage: false,
		resendPinLoader: false,
		showPasswordPolicyError: false
	};

	componentDidMount() {
		// Check for navigation state for password policy error
		const navigationState = this.props?.location?.state || this.props?.navigate?.state;
		if (navigationState && navigationState.passwordPolicyError) {
			const email = navigationState.email || "";
			this.setState({
				showPasswordPolicyError: true,
				email: email
			});
		}
	}

	verificationLinkEndPoint: string = getConfigProperty('apiEndPoint')['verificationLink'];
	accessToken = BASIC_AUTH_TOKEN;
	axiosDefaults: any = {
		"baseUrl": "",
		"headers": {
			"Access-Control-Allow-Origin": "*",
			"Authorization": `Basic ${this.accessToken}`,
			"Content-Type": "application/json"
		}
	};

	Step1Schema = Yup.object().shape({
		email: Yup.string().email().required("Email required")
			.matches(
				/^(([^<>()\],;:\s@]+(\.[^<>()\],;:\s@]+)*)|(.+))@(([^<>()[\],;:\s@]+\.)+[^<>()[\],;:\s@"]{2,})$/,
				("Email required")
			),
	});

	schemaArray = [this.Step1Schema];

	nextStep = (props: any) => {
		props.submitForm().then(() => {
			if (props.isValid) {
				this.setState((state) => ({
					currentStep: Math.min(state.currentStep + 1, state.lastStep),
				}));
				props.validateForm();
				props.setTouched({});
			}
		});
	};

	handleSubmit = (values: any, formikbag: any) => {
		const {
			translate
		} = this.props;
		this.setState({ email: values.email });
		setTimeout(() => {
			formikbag.setSubmitting(false);
		}, 1000);
		axios.defaults.baseURL = this.axiosDefaults.baseURL;
		axios.defaults.headers.post = this.axiosDefaults.headers;
		axios
			.post(
				this.verificationLinkEndPoint,
				{
					"organisation": sessionStorage.getItem("organisation"),
					"email": `${values.email}`
				}
			)
			.then((response) => {
				this.setState({ loaded: true, email: values.email });
				if (!sessionStorage.getItem("access token")) {
					sessionStorage.setItem("access token", this.accessToken);
				}
			})
			.catch((error) => {
				if (error.response.status !== 200) {
					this.setState((state) => ({
						loaded: false,
						isError: true,
						serverError: translate('reset.password.form.error.message.email'),//"Account not found. Please re-enter email",
						currentStep: Math.max(state.currentStep - 1, 0),
					}));
				}
			});
	};

	resendVerification = (e: any) => {
		e.preventDefault();
		this.setState({ resendCount: this.state.resendCount + 1, secondMessage: true, resendPinLoader: true });
		const { getServicesErrorObj } = this.props;
		if (this.state.resendCount >= 1) {
			this.setState({ resendDisabled: true });
		}
		axios.defaults.baseURL = this.axiosDefaults.baseURL;
		axios.defaults.headers.post = this.axiosDefaults.headers;
		axios
			.post(
				this.verificationLinkEndPoint,
				{
					organisation: sessionStorage.getItem("organisation"),
					email: this.state.email
				}
			)
			.then(() => {
				this.setState({ loaded: true, resendPinLoader: false });
			})
			.catch((error) => {
				if (error.status !== 200) {
					this.setState(() => ({
						loaded: false,
						isError: true,
						resendPinLoader: false,
						serverError: "Access not permitted",
					}));
				}
				getServicesErrorObj(error);
			});
	};

	loginPath(path: string) {
		this.props.navigate(path);
	}

	step1 = (props: any) => {
		if (this.state.currentStep === 0) {
			const { classes, translate } = this.props;
			return (
				<>
					{this.state.showPasswordPolicyError && (
						<APErrorMessage errorMessage={translate('login.form.password.policy.error')} />
					)}
					{this.state.isError ? (<APErrorMessage errorMessage={this.state.serverError} />) : ("")}
					<div className={classes.resetPasswordIntro}>
						<APHeading className={classes.resetPasswordIntroHeader} variant="h4">
							{translate("reset.password.intro.header.step.one")}
						</APHeading>
						<APLabel className={classes.resetPasswordIntroText}>
							{translate("reset.password.intro.text.step.one")}
						</APLabel>
					</div>

					<div className={classes.inputsSurround}>
						<APInput
							data-testid='resetEmailInput'
							type="email"
							label={translate("login.form.email.label")}
							name="email"
							className={classes.resetPasswordEmailInput}
							error={props.errors.email && props.touched.email}
							helperText={props.errors.email && props.touched.email && translate('login.form.email.error')}
							value={props.values.email}
							onChange={props.handleChange}
							onBlur={props.handleBlur}
							inputProps={{ tabIndex: "1" }}
							isCoralogixMasked={true}
						/>
					</div>

					<div className={classes.resetPasswordActions}>
						<APButton
							data-testid='loginButton'
							className={classes.resetPasswordButtons}
							type="button"
							variant="outlined"
							color="primary"
							onClick={() => this.loginPath("/login")}
							tabIndex={3}
							disableRipple={true}
						>
							{translate("reset.password.form.action.back.login.label")}
						</APButton>
						<APButton
							data-testid='resetPasswordButton'
							className={classes.resetPasswordButtons}
							type="button"
							variant="contained"
							color="primary"
							disabled={!(props.isValid && (this.state.showPasswordPolicyError || props.dirty))}
							onClick={() => {
								this.nextStep(props);
							}}
							tabIndex={2}
							disableRipple={true}
						>
							{translate("reset.password.form.action.reset.password.label")}
						</APButton>
					</div>
				</>
			);
		}
	};

	step2 = (props: any) => {
		if (this.state.currentStep !== 1) return null;

		const { classes, translate } = this.props;
		const { loaded, secondMessage, resendPinLoader, resendDisabled } = this.state;

		if (!loaded) {
			return (
				<div className={classes.resetPasswordLinkSection}>
					<APLoadingSpinner />
				</div>
			);
		}

		return (
			<>
				{this.renderIntroMessage(translate, classes, props.values.email, secondMessage)}
				{this.renderIconSection(classes, resendPinLoader, secondMessage)}
				{!resendPinLoader && this.renderResendEmailSection(classes, translate, resendDisabled, this.resendVerification)}
			</>
		);
	};

	// Helper Functions
	renderIntroMessage = (translate, classes, email, secondMessage) => (
		<div className={`${classes.resetPasswordIntro} ${classes.resetPasswordIntroSent}`}>
			<APHeading className={classes.resetPasswordIntroHeader} variant="h4">
				{secondMessage
					? translate("reset.password.intro.header.step.two.again")
					: translate("reset.password.intro.header.step.two")}
			</APHeading>
			<APLabel className={classes.resetPasswordIntroText}>
				{translate("reset.password.intro.text.step.two.para.one")}&nbsp;{email}.<br />
				{translate("reset.password.intro.text.step.two.para.two")}
			</APLabel>
		</div>
	);

	renderIconSection = (classes, resendPinLoader, secondMessage) => (
		<div className={classes.inputsSurround}>
			{secondMessage && resendPinLoader ? (
				<div className={classes.resendPasswordLoader}><APLoadingSpinner /></div>
			) : (
				<APResetImagePlaceholder>
					<VerifiedEmail />
				</APResetImagePlaceholder>
			)}
		</div>
	);

	renderResendEmailSection = (classes, translate, resendDisabled, resendVerification) => (
		<div className={classes.resetPasswordLinkSection}>
			<Typography>
				{translate("reset.password.form.action.resend.email.copy")}
				{resendDisabled ? (
					<>&nbsp;{translate("reset.password.form.action.resend.email.link")}</>
				) : (
					<Link
						data-testid="resendVerificationLink"
						to="/resetpassword"
						onClick={resendVerification}
					>
						{translate("reset.password.form.action.resend.email.link")}
					</Link>
				)}
			</Typography>
		</div>
	);

	render() {
		const { currentStep } = this.state;
		const { schemaArray } = this;
		const { classes, settings } = this.props;
		sessionStorage.setItem("organisation", settings.ORG_CODE);

		return (
			<div className={classes.resetPasswordContainer}>
				<div className={classes.resetPasswordContent}>
					<Formik
						onSubmit={this.handleSubmit}
						enableReinitialize
						initialValues={{ email: this.state.email }}
						validationSchema={schemaArray[currentStep]}
					>
						{(props: any) => (
							<Form>
								{this.step1(props)}
								{this.step2(props)}
							</Form>
						)}
					</Formik>
				</div>
			</div>
		);
	}
}

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	getServicesErrorObj: servicesDowntimeAction.getServicesErrorObj
}, dispatch);

export default connect(null, mapDispatchToProps)(
	withNavigation(withTenant(withTranslate(
		withStyles(ResetPasswordContainerStyles)(ResetPasswordContainer)
	)
	)
	)
);
